//node_modules
import React, { FunctionComponent, useState, useCallback } from "react";
import { Form, FloatingLabel, Row, Col, Card } from "react-bootstrap";
import capitalize from "lodash/capitalize";

//alerts
import { useAlert } from "@adjust/website/src/components/AlertComponent";

//components
import SubmitButton from "@adjust/website/src/components/SubmitButtonComponent";

//emotion
import { css } from "@adjust/libs/emotion";

//locals
import CountryCodeComponent from "./CountryCodeComponent";

//state
import useClassState from "libs/useClassState";
import FormData from "../states/FormData";

//api
import createContact from "src/templates/api/contact/fetch";
import { contacts, ContactFormSchema } from "src/templates/api/contact/validation";

const initialValues: ContactFormSchema = {
  fullname: "",
  phone: "",
  email: "",
  contact: contacts[0],
  message: "",
};

type FormProps = {
  setSubmitted: (value: boolean) => void;
};

// ------------------------------------
// Styles
// ------------------------------------

const cardCss = css({
  border: "none",
  borderRadius: "1rem",
  boxShadow: "0 6px 18px rgba(0,0,0,.06)",
});

const labelCss = css({
  "& label": {
    color: "#6c757d",
  },
});

const textAreaCss = css({
  resize: "vertical",
  minHeight: "220px !important",
});

const submitContainer = css({
  textAlign: "center",
  marginTop: "1.5rem",
});

const submitButton = css({
  minWidth: "260px",
  borderRadius: "999px",
  paddingTop: "0.9rem",
  paddingBottom: "0.9rem",
  fontWeight: 600,
  fontSize: "1.05rem",
});

// ------------------------------------

const FormComponent: FunctionComponent<FormProps> = ({ setSubmitted }) => {
  const makeAlert = useAlert();

  const [submitting, setSubmitting] = useState(false);

  const form = useClassState(FormData, [initialValues]);

  const onValid = useCallback(
    (data: ContactFormSchema) => {
      setSubmitting(true);

      createContact(data)
        .then(() => setSubmitted(true))
        .catch((error) => {
          setSubmitting(false);
          makeAlert(`Failed to submit: ${error.message}`);
        });
    },
    [makeAlert, setSubmitted],
  );

  return (
    <Form onSubmit={form.handleSubmit(onValid)}>
      <Card className={cardCss}>
        <Card.Body className="p-4 p-lg-5">
          <div className="text-center mb-5">
            <h2 className="fw-bold">Ready to Explore the World?</h2>

            <p className="text-muted mb-0">
              Every unforgettable journey begins with a conversation! Reserve your Complimentary Travel Planning Session, and let's start creating the vacation you've been dreaming
              about!
            </p>
          </div>
          <Row className="g-3">
            <Col md={6}>
              <FloatingLabel className={labelCss} controlId="contact-fullname" label="Full Name">
                <Form.Control
                  type="text"
                  placeholder="Full Name"
                  name="fullname"
                  value={form.value("fullname")}
                  onBlur={form.onBlur("fullname")}
                  onChange={form.onChange("fullname")}
                  isValid={form.isFieldValid("fullname")}
                  isInvalid={form.isFieldInvalid("fullname")}
                  required
                />

                <Form.Control.Feedback type="valid">Looks good!</Form.Control.Feedback>

                <Form.Control.Feedback type="invalid">A full name is required.</Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6}>
              <CountryCodeComponent value={form.code} setValue={form.setCode} />
            </Col>
            <Col md={6}>
              <FloatingLabel className={labelCss} controlId="contact-phone" label="Phone Number">
                <Form.Control
                  type="tel"
                  placeholder="Phone Number"
                  name="phone"
                  value={form.value("phone")}
                  onBlur={form.onBlur("phone")}
                  onChange={form.onChange("phone")}
                  isValid={form.isFieldValid("phone")}
                  isInvalid={form.isFieldInvalid("phone")}
                  required
                />

                <Form.Control.Feedback type="valid">Looks good!</Form.Control.Feedback>

                <Form.Control.Feedback type="invalid">A valid phone number is required.</Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6}>
              <FloatingLabel className={labelCss} controlId="contact-email" label="Email Address">
                <Form.Control
                  type="email"
                  placeholder="Email Address"
                  name="email"
                  value={form.value("email")}
                  onBlur={form.onBlur("email")}
                  onChange={form.onChange("email")}
                  isValid={form.isFieldValid("email")}
                  isInvalid={form.isFieldInvalid("email")}
                  required
                />

                <Form.Control.Feedback type="valid">Looks good!</Form.Control.Feedback>

                <Form.Control.Feedback type="invalid">A valid email is required.</Form.Control.Feedback>
              </FloatingLabel>
            </Col>
            <Col md={6}>
              <FloatingLabel className={labelCss} controlId="contact-contact" label="Preferred Contact">
                <Form.Select
                  name="contact"
                  value={form.value("contact")}
                  onBlur={form.onBlur("contact")}
                  onChange={form.onChange("contact")}
                  isValid={form.isFieldValid("contact")}
                  isInvalid={form.isFieldInvalid("contact")}
                >
                  {contacts.map((contact) => (
                    <option key={contact} value={contact}>
                      {capitalize(contact)}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </Col>
            <Col xs={12}>
              <FloatingLabel
                className={labelCss}
                controlId="contact-message"
                label="Briefly describe what kind of trip you would like our help planning and the dates for your trip"
              >
                <Form.Control
                  as="textarea"
                  className={textAreaCss}
                  placeholder="Briefly describe what kind of trip you would like our help planning and the dates for your trip"
                  name="message"
                  value={form.value("message")}
                  onBlur={form.onBlur("message")}
                  onChange={form.onChange("message")}
                  isValid={form.isFieldValid("message")}
                  isInvalid={form.isFieldInvalid("message")}
                  required
                />
              </FloatingLabel>
            </Col>
          </Row>

          <div className={submitContainer}>
            <SubmitButton submitting={submitting} variant="primary" size="lg" className={submitButton}>
              Send My Travel Request
            </SubmitButton>
          </div>
        </Card.Body>
      </Card>
    </Form>
  );
};

export default React.memo(FormComponent);
